import { supabase } from './supabase';
import { 
  AITouchupOperation, 
  CreateAITouchupOperationRequest, 
  UpdateAITouchupOperationRequest 
} from '../types/ai-touchup';

export const aiTouchupService = {
  // Create a new AI touchup operation
  async createOperation(
    userId: string,
    request: CreateAITouchupOperationRequest
  ): Promise<AITouchupOperation> {
    console.log('💾 Creating AI touchup operation:', request.processing_id);
    
    const { data, error } = await supabase
      .from('ai_touchup_operations')
      .insert({
        user_id: userId,
        processing_id: request.processing_id,
        operation_type: request.operation_type,
        prompt: request.prompt,
        original_image_url: request.original_image_url,
        mask_data: request.mask_data,
        tokens_spent: request.tokens_spent || 0,
        webhook_url: request.webhook_url,
        processing_status: 'processing'
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating AI touchup operation:', error);
      throw error;
    }

    console.log('✅ AI touchup operation created:', data.id);
    return data;
  },

  // Update an existing AI touchup operation
  async updateOperation(
    processingId: string,
    updates: UpdateAITouchupOperationRequest
  ): Promise<AITouchupOperation> {
    console.log('🔄 Updating AI touchup operation:', processingId);
    
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    if (updates.processing_status === 'completed') {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('ai_touchup_operations')
      .update(updateData)
      .eq('processing_id', processingId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating AI touchup operation:', error);
      throw error;
    }

    console.log('✅ AI touchup operation updated:', data.id);
    return data;
  },

  // Get all operations for a user
  async getUserOperations(userId: string): Promise<AITouchupOperation[]> {
    const { data, error } = await supabase
      .from('ai_touchup_operations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching user AI touchup operations:', error);
      throw error;
    }

    return data || [];
  },

  // Get a specific operation by processing ID
  async getOperationByProcessingId(processingId: string): Promise<AITouchupOperation | null> {
    const { data, error } = await supabase
      .from('ai_touchup_operations')
      .select('*')
      .eq('processing_id', processingId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows returned
        return null;
      }
      console.error('❌ Error fetching AI touchup operation:', error);
      throw error;
    }

    return data;
  },

  // Mark operation as failed
  async markOperationFailed(
    processingId: string,
    errorMessage: string
  ): Promise<AITouchupOperation> {
    return this.updateOperation(processingId, {
      processing_status: 'failed',
      error_message: errorMessage,
      completed_at: new Date().toISOString()
    });
  },

  // Mark operation as completed with result
  async markOperationCompleted(
    processingId: string,
    resultImageUrl: string,
    n8nResponse?: any
  ): Promise<AITouchupOperation> {
    return this.updateOperation(processingId, {
      processing_status: 'completed',
      result_image_url: resultImageUrl,
      n8n_response: n8nResponse,
      completed_at: new Date().toISOString()
    });
  }
}; 